import { computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { WHLoginDataService } from '@workheld/workheld-shared-lib';

const DEFAULT_TENANT_TOKENS = ['workheldflowlivetest', 'workheld'];
/** sessionStorage key of the last AI conversation; cleared on logout. */
export const AI_SESSION_STORAGE_KEY = 'wh-ai-session';
/** Used when the tenant has no AI assistant name configured. */
export const DEFAULT_AI_ASSISTANT_NAME = 'AI Assistant';

type AiState = {
  isAiActive: boolean;
};

/**
 * Whether the Workheld AI panel is open. Everything else (sessions, history,
 * feedback, documents) is handled by <wh-ai-chat-panel> from the shared lib.
 */
export const AiStore = signalStore(
  { providedIn: 'root' },
  withState<AiState>({ isAiActive: false }),
  withComputed((_, loginDataService = inject(WHLoginDataService)) => {
    const activeTenant = toSignal(loginDataService.activeTenantDOM$);
    return {
      isDefaultTenant: computed(() =>
        DEFAULT_TENANT_TOKENS.includes(
          loginDataService.activeTenantDOM$?.value?.token,
        ),
      ),
      /** Configured per tenant in the management client. */
      assistantName: computed(
        () =>
          activeTenant()?.customization?.aiAssistantName?.trim() ||
          DEFAULT_AI_ASSISTANT_NAME,
      ),
      /** File URL of the tenant's assistant logo; no logo when not configured. */
      assistantLogoUrl: computed(
        () => activeTenant()?.customization?.aiAssistantLogo?.file ?? null,
      ),
    };
  }),
  withMethods((store) => ({
    setIsAiActive(isActive: boolean): void {
      patchState(store, { isAiActive: isActive });
    },
    toggleAiActive(): void {
      patchState(store, { isAiActive: !store.isAiActive() });
    },
  })),
);
